/**
 * Staleness + reconciliation contract (WR1 SR1.1) — the freshness half of the
 * pure reorder engine. Zero I/O, no globals, no `Date.now`: the caller supplies
 * both the snapshot's `syncedAt` and the read moment `now`, so the MSW handler,
 * the UI, and Joe's future backend can never drift on "is this snapshot old?".
 *
 * The staleness contract is ADVISORY, NEVER BLOCKING (plan §Staleness contract,
 * Appendix D-9): a stale or expired read-through still yields a fully actionable
 * suggestion — it only downgrades `confidence` so the UI can warn. Freshness is
 * owned SOLELY by this tier (surfaced on the 可供量 SourceStampTag); the 低信心
 * badge rolls up only the NON-freshness causes (variance + thin history), so one
 * cause never lights two chips on the same row (UX-lens adjustment 2 / Q7).
 *
 * Thresholds (24h / 72h) and the reconciliation tolerance (±10%) are DEFAULTS
 * pending the real sync cadence (OQ-10) — centralised here so a ruling is a
 * one-line constant change, not a formula rewrite.
 */

// ---------------------------------------------------------------------------
// Config constants (OQ-10 defaults)
// ---------------------------------------------------------------------------

const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * Freshness tiers by age of the read-through snapshot:
 * - `fresh`   < 24h
 * - `stale`   24h – 72h  (SourceStampTag warning; confidence → low)
 * - `expired` > 72h       (banner 資料同步過期; rows stay visible AND actionable)
 */
export const STALENESS_THRESHOLDS = {
  /** A snapshot at or beyond this age (hours) is at least `stale`. */
  staleAfterHours: 24,
  /** A snapshot strictly beyond this age (hours) is `expired`. */
  expiredAfterHours: 72,
} as const;

/**
 * Tier-1 ↔ Tier-2 on-order reconciliation tolerance (percent of the Tier-1
 * `qtyOnOrder` reference). A |delta| beyond this band renders a 差異 chip and
 * downgrades suggestion confidence (never the engine input — Appendix D-1).
 */
export const RECONCILIATION_TOLERANCE_PCT = 10;

// ---------------------------------------------------------------------------
// Freshness
// ---------------------------------------------------------------------------

export type FreshnessTier = 'fresh' | 'stale' | 'expired';

/**
 * Whole-and-fractional hours between the snapshot stamp and `now`, clamped at
 * 0. Unparseable stamps or backward clock skew both collapse to 0 (treated as
 * fresh) — the contract is advisory, so an unreadable stamp must never
 * fabricate staleness or throw.
 */
export function hoursSince(syncedAt: string, now: string): number {
  const elapsedMs = Date.parse(now) - Date.parse(syncedAt);
  if (!Number.isFinite(elapsedMs)) return 0;
  return Math.max(0, elapsedMs / MS_PER_HOUR);
}

/**
 * Classify a snapshot's freshness. Boundaries: `< 24h` fresh, `24h–72h`
 * inclusive stale, `> 72h` expired.
 */
export function freshnessTierOf(syncedAt: string, now: string): FreshnessTier {
  const hours = hoursSince(syncedAt, now);
  if (hours < STALENESS_THRESHOLDS.staleAfterHours) return 'fresh';
  if (hours <= STALENESS_THRESHOLDS.expiredAfterHours) return 'stale';
  return 'expired';
}

/** A tier that downgrades confidence (anything other than `fresh`). */
export function isStaleTier(tier: FreshnessTier): boolean {
  return tier !== 'fresh';
}

// ---------------------------------------------------------------------------
// Reconciliation (Tier-1 qtyOnOrder echo vs Tier-2 qtyOpenToReceive)
// ---------------------------------------------------------------------------

/**
 * `netsuiteDelta = qtyOnOrder(Tier-1) − qtyOpenToReceive(Tier-2)`. `null` when
 * the Tier-1 echo is not synced (nothing to reconcile against).
 */
export function reconciliationDelta(
  tier1QtyOnOrder: number | null | undefined,
  tier2QtyOpenToReceive: number
): number | null {
  if (tier1QtyOnOrder == null) return null;
  return tier1QtyOnOrder - tier2QtyOpenToReceive;
}

/**
 * Whether the Tier-1/Tier-2 on-order figures disagree beyond tolerance. The
 * band is a percentage of the Tier-1 reference; when the reference is 0 the
 * band is 0, so any nonzero delta trips it. Not-synced (`null`) never counts as
 * a variance — there is simply no reference.
 */
export function isReconciliationVariance(
  tier1QtyOnOrder: number | null | undefined,
  tier2QtyOpenToReceive: number,
  tolerancePct: number = RECONCILIATION_TOLERANCE_PCT
): boolean {
  const delta = reconciliationDelta(tier1QtyOnOrder, tier2QtyOpenToReceive);
  if (delta === null) return false;
  const band = Math.abs(tier1QtyOnOrder as number) * (tolerancePct / 100);
  return Math.abs(delta) > band;
}
